"use client"

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, User, Mail, Phone, MapPin, Globe } from "lucide-react";
import { Turnstile } from '@marsidev/react-turnstile';

export default function JoinUsPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    address: "",
    city: "",
    state: "",
    country: "",
  });

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [isFormValid, setIsFormValid] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  // 🏛️ ELITE VALIDATION LOGIC: Strictly compulsory check
  useEffect(() => {
    const isValid = Object.values(formData).every((val) => val.trim().length > 0);
    setIsFormValid(isValid);
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    if (!turnstileToken) {
      alert("Please wait for security verification to complete.");
      return;
    }

    setStatus("loading");
    try {
      const res = await fetch("/api/recruitment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      if (res.ok) {
        setStatus("success");
        setTurnstileToken("");
        setFormData({
          fullName: "",
          email: "",
          mobile: "",
          address: "",
          city: "",
          state: "",
          country: "",
        });
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  const inputClasses = "w-full bg-white/5 border border-white/10 px-5 py-4 text-sm font-serif outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all text-white placeholder:text-zinc-600";

  return (
    <div className="min-h-screen bg-[#020202] text-white pt-32 pb-20 relative overflow-hidden">
      <style dangerouslySetInnerHTML={{
        __html: `
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus,
        textarea:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #18181b inset !important;
            -webkit-text-fill-color: white !important;
            transition: background-color 5000s ease-in-out 0s;
        }
      `}} />

      {/* Premium Background FX */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]" />
      <div className="absolute bottom-1/4 -right-1/4 w-[800px] h-[800px] bg-brand/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-8 max-w-[1200px] relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">

          {/* Left: The Manifesto */}
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-6 relative">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                <span className="text-brand font-black uppercase tracking-[0.4em] text-[10px]">Institutional Expansion</span>
              </div>
              <h1 className="brand-logo-font text-5xl md:text-8xl leading-[0.9] text-white tracking-tight">
                Intelligence <br/> Bureau.
              </h1>
              <p className="font-serif italic text-xl md:text-2xl text-zinc-400 leading-tight">
                "Recruiting the next generation of global strategic analysts and ground-truth journalists."
              </p>
            </div>

            <div className="space-y-8 pt-8 border-t border-white/10 text-sm md:text-base font-serif text-zinc-300 leading-relaxed">
              <p>World Prime Post functions as a high-fidelity intelligence terminal. We don't just report news; we decode the global shift. If you have the rigor to analyze geopolitical nodes and the digital economy, we want you on our global desk.</p>

              <div className="grid grid-cols-1 gap-4 pt-4">
                {[
                  "Global Distribution Reach",
                  "High-Authority Editorial Support",
                  "Decentralized Working Infrastructure"
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + (idx * 0.1) }}
                    className="flex items-center gap-4 group p-4 border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-brand/30 transition-all"
                  >
                    <div className="w-2 h-2 bg-brand/50 group-hover:bg-brand transition-colors shadow-[0_0_8px_rgba(255,0,0,0.5)]" />
                    <span className="text-[11px] font-black uppercase tracking-[0.2em] text-zinc-300 group-hover:text-white transition-colors">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: The Secure Submission Vault */}
          <div className="lg:col-span-7 relative mt-8 lg:mt-0">
            {/* Tech Vault Design */}
            <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-3xl border border-white/10 shadow-2xl" 
                 style={{ clipPath: 'polygon(0 0, calc(100% - 30px) 0, 100% 30px, 100% 100%, 30px 100%, 0 calc(100% - 30px))' }} />
            
            {/* Edge Accents */}
            <div className="absolute top-0 right-[30px] w-8 h-px bg-brand" />
            <div className="absolute bottom-0 left-[30px] w-8 h-px bg-brand" />

            <div className="relative p-8 md:p-12 z-10">
              <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                <h3 className="text-sm font-mono text-zinc-400 uppercase tracking-widest">Application Dossier</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
                  <span className="text-[9px] text-brand font-mono uppercase tracking-widest">Secure Upload</span>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="h-full flex flex-col items-center justify-center text-center space-y-6 py-20"
                  >
                    <div className="w-20 h-20 bg-brand/10 flex items-center justify-center border border-brand/40" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
                      <CheckCircle2 className="w-10 h-10 text-brand" />
                    </div>
                    <h3 className="text-3xl font-serif font-bold text-white">Dossier Received.</h3>
                    <p className="text-sm text-zinc-400 font-mono max-w-[320px] leading-relaxed">Our institutional recruitment board will review your credentials and contact you shortly.</p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-8 relative z-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {/* Invisible Cloudflare Turnstile */}
                    <div className="w-full flex justify-center hidden">
                      <Turnstile
                        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                        onSuccess={(token) => setTurnstileToken(token)}
                        options={{ theme: 'dark' }}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Identity Details</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                        <input
                          type="text" required placeholder="Full Legal Name" className={`${inputClasses} pl-12 bg-black/50`}
                          value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Professional Email</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="email" required placeholder="Email Address" className={`${inputClasses} pl-12 bg-black/50`}
                            value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Contact Number</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                          <input
                            type="tel" required placeholder="Mobile with Country Code" className={`${inputClasses} pl-12 bg-black/50`}
                            value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Residency / Address</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                        <input
                          type="text" required placeholder="Full Residential / Office Address" className={`${inputClasses} pl-12 bg-black/50`}
                          value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      <input
                        type="text" required placeholder="City" className={`${inputClasses} bg-black/50`}
                        value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                      <input
                        type="text" required placeholder="State" className={`${inputClasses} bg-black/50`}
                        value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      />
                      <div className="relative col-span-2 md:col-span-1">
                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                        <input
                          type="text" required placeholder="Country" className={`${inputClasses} pl-12 bg-black/50`}
                          value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <button
                        type="submit"
                        disabled={!isFormValid || status === "loading"}
                        className="w-full bg-brand hover:bg-brand-light py-5 text-[12px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 transition-all disabled:opacity-30 disabled:cursor-not-allowed group text-white"
                        style={{ clipPath: 'polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                      >
                        {status === "loading" ? "UPLOADING DOSSIER..." : (
                          <>
                            TRANSMIT APPLICATION <Send className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-[8px] text-zinc-500 text-center uppercase tracking-widest font-mono">
                      Your information is kept private and secure. Our team will review your application.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
